import firstInitStore from '@/stores/firstInit';
import sessionStore from '@/stores/session';
import { DIALOGUE_FROM, DIALOGUE_TYPE } from '@/types/common';
import reporter from './report';

const delay = 150;
function createAsyncTaskExecutor(
  executor: (progress: number) => void,
  options: {
    minLimit: number;
    maxLimit: number;
  },
) {
  const { minLimit, maxLimit } = options;

  const callbacks: Function[] = [];
  const registerCallback = (cb: () => void) => {
    callbacks.push(cb);
  };

  let progress = minLimit;
  const asyncTask = () => {
    setTimeout(() => {
      progress += 1;
      executor(progress);

      if (progress >= maxLimit) {
        setTimeout(() => {
          callbacks.forEach((cb) => cb?.());
        }, delay);
        return;
      }

      asyncTask();
    }, delay);
  };

  return { asyncTask, registerCallback };
}

const isBrRegion = COUNTRY === 'br';
const skeletonDialogues = [
  DIALOGUE_TYPE.Announcements,
  DIALOGUE_TYPE.Msg,
  DIALOGUE_TYPE.HotQuestionCategory,
  DIALOGUE_TYPE.IntentHotQuestion,
];

// 骨架屏加载分为两步，第一步是 Announcements， HotQuestionCategory，Greeting，
// 第二部是IntentHotQuestion和ShortCut, 两步也分别对应着第一和第二次init session请求。
const defaultStartStep = 0;
const firstStepOverProgress = 3;
const secondStepOverProgress = 4;

// load skeleton start
const { asyncTask, registerCallback: registerRenderCallback } = createAsyncTaskExecutor(
  (progress) => {
    renderState.progress = progress;
    addSkeletonDialogue(skeletonDialogues[progress - 1]);
    if (progress === secondStepOverProgress) {
      // 第一次初始化获取的消息卡片骨架图已经加载完成，开始加载第二次此时要触发shortcut的骨架图
      firstInitStore.UPDATE_CAN_SHOW_SHORTCUT(true);
      firstInitStore.UPDATE_IS_SHOW_SHORTCUT_SKELETON(true);
    }
  },
  {
    minLimit: isBrRegion ? defaultStartStep : firstStepOverProgress,
    maxLimit: secondStepOverProgress,
  },
);

const renderState = { progress: defaultStartStep };
export function progressRenderSkeletonScreen() {
  if (isBrRegion) {
    asyncTask();
    return;
  }

  const initRenderDialogues = skeletonDialogues.slice(defaultStartStep, firstStepOverProgress);
  initRenderDialogues.forEach(addSkeletonDialogue);
  asyncTask();
}

const addSkeletonDialogue = (dialogueType: DIALOGUE_TYPE) => {
  const dialogue = {
    dialogueId: '',
    from: DIALOGUE_FROM.BOT,
    type: dialogueType,
    skeleton: true,
    data: {},
  };

  sessionStore.ADD_INIT_LIST(dialogue);
};
// load skeleton end

// replace real dialogue start
const firstInitSessionDialogues: Record<string, any>[] = [];
const initSessionDialogues: Record<string, any>[] = [];
export function stashInitSessionDialogue(dialogue: any, isFirstInitSession: boolean) {
  if (isFirstInitSession) {
    firstInitSessionDialogues.push(dialogue);
  } else {
    initSessionDialogues.push(dialogue);
  }
}

function clearAllFootPrint() {
  sessionStore.REMOVE_ALL_INIT_SKELETON_DIALOGUE();
  firstInitStore.UPDATE_IS_SHOW_SHORTCUT_SKELETON(false);
}

const checkGreetingAndHotQuestionCategoryOrder = () => {
  const greeting = firstInitSessionDialogues.findIndex((d) => d.type === DIALOGUE_TYPE.Msg);
  const hotQuestionCategory = firstInitSessionDialogues.findIndex((d) => d.type === DIALOGUE_TYPE.HotQuestionCategory);
  return hotQuestionCategory < greeting && hotQuestionCategory !== -1;
};

const replaceState = {
  firstReplaceIsComplete: false,
  // 两次init session网络请求是否完成。
  requestIsComplete: { firstInitSession: false, initSession: false },
};

export function replaceSkeletonDialogues(isFirstInitSession: boolean) {
  replaceSkeletonDialoguesImpl(isFirstInitSession, false);
}

function replaceSkeletonDialoguesAsyncTask(isFirstInitSession: boolean) {
  replaceSkeletonDialoguesImpl(isFirstInitSession, true);
}

function replaceSkeletonDialoguesImpl(isFirstInitSession: boolean, isAsyncTask = false) {
  const { progress } = renderState;
  if (!isAsyncTask) {
    isFirstInitSession
      ? (replaceState.requestIsComplete.firstInitSession = true)
      : (replaceState.requestIsComplete.initSession = true);
  }

  if (isFirstInitSession) {
    // 第一次init session dialogue替换
    if (progress >= firstStepOverProgress) {
      if (firstInitSessionDialogues.length !== 0) {
        const { asyncTask: firstReplaceAsyncTask, registerCallback } = createAsyncTaskExecutor(
          (progress) => {
            const dialogue = firstInitSessionDialogues[progress - 1];

            // HotQuestionCategory在Greeting前面
            const categoryInFrontOfGreeting = checkGreetingAndHotQuestionCategoryOrder();
            const needChangeOrder = dialogue.type === DIALOGUE_TYPE.HotQuestionCategory && categoryInFrontOfGreeting;
            replaceInitSkeletonDialogue(dialogue, { isRemoveBefore: !needChangeOrder, isChangeOrder: needChangeOrder });
          },
          {
            minLimit: 0,
            maxLimit: firstInitSessionDialogues.length,
          },
        );

        registerCallback(() => {
          replaceState.firstReplaceIsComplete = true;
          replaceSkeletonDialoguesAsyncTask(false);
        });
        firstReplaceAsyncTask();
      }
    } else {
      registerRenderCallback(() => replaceSkeletonDialoguesAsyncTask(true));
    }
  } else {
    // 第二次init session dialogue替换
    if (progress >= secondStepOverProgress && replaceState.firstReplaceIsComplete) {
      if (initSessionDialogues.length !== 0) {
        const { asyncTask, registerCallback } = createAsyncTaskExecutor(
          (progress) => {
            const dialogue = initSessionDialogues[progress - 1];
            // 二次 init session 返回的可能是不只有3*3，也有可能是order card，还有可能是多个reply, 所以这里简单处理，强制替换或增加卡片
            replaceInitSkeletonDialogue(dialogue, { isRemoveBefore: true, isChangeOrder: false, forceReplace: true });
            firstInitStore.UPDATE_IS_SHOW_SHORTCUT_SKELETON(false);
          },
          {
            minLimit: 0,
            maxLimit: initSessionDialogues.length,
          },
        );
        asyncTask();
        registerCallback(() => {
          clearAllFootPrint();

          firstScreenReport.complete();
          firstScreenReport.totalLoadTime();
        });
      } else if (replaceState.requestIsComplete.initSession) {
        clearAllFootPrint();

        firstScreenReport.complete();
        firstScreenReport.totalLoadTime();
      }
    }
  }
}

// 更新某条骨架屏dialogue（通过dialogue type）
export const replaceInitSkeletonDialogue = (
  dialogue: any,
  options: { isRemoveBefore: boolean; isChangeOrder: boolean; forceReplace?: boolean },
) => {
  const { isRemoveBefore, isChangeOrder, forceReplace } = options;
  if (forceReplace) {
    const index = sessionStore.initList.findIndex((d) => d.skeleton);
    if (index !== -1) {
      // 替换真实dialogue
      sessionStore.REPLACE_INIT_DIALOGUE({ obj: dialogue, index });
    } else {
      sessionStore.ADD_INIT_LIST(dialogue);
    }
  } else {
    const index = sessionStore.initList.findIndex((d) => d.skeleton && d.type === dialogue.type);
    if (index !== -1) {
    // 替换真实dialogue
      sessionStore.REPLACE_INIT_DIALOGUE({ obj: dialogue, index });

      if (isRemoveBefore) {
      // 删除没有返回真实dialogue的skeleton
        sessionStore.REMOVE_INIT_DIALOGUE_BEFORE_INDEX(index);
      }

      if (isChangeOrder) {
        sessionStore.CHANGE_INIT_DIALOGUE_ORDER(index - 1, index);
      }
    }
  }

  firstScreenReport.loadTime(dialogue.type);
};
// replace real dialogue end

// 当 init session 失败时，需要去掉骨架屏卡片
export function handleFirstScreenSkeletonWhenInitSessionFail(isFirstInitSession = true) {
  const { progress } = renderState;
  if (progress < secondStepOverProgress) {
    registerRenderCallback(() => handleFirstScreenSkeletonWhenInitSessionFail(isFirstInitSession));
    return;
  }

  const index = sessionStore.initList.findIndex((d) => d.skeleton && d.type === DIALOGUE_TYPE.IntentHotQuestion);
  if (isFirstInitSession) {
    sessionStore.REMOVE_INIT_DIALOGUE_BEFORE_INDEX(index + 1);
  } else {
    sessionStore.REMOVE_INIT_DIALOGUE(index);
  }

  firstInitStore.UPDATE_IS_SHOW_SHORTCUT_SKELETON(false);
}

// 监控上报
const firstScreenReport = {
  complete: () => {
    reporter.apiBatchReport({ type: 'count', value: { label: 'first_screen_load_complete' }});
  },
  totalLoadTime: () => {
    reporter.apiBatchReport({
      type: 'time',
      value: { label: 'first_screen_total_load_time', cost: window.performance.now() },
    });
  },
  loadTime: (module: DIALOGUE_TYPE) => {
    reporter.apiBatchReport({
      type: 'time',
      value: { label: 'first_screen_load_time', cost: window.performance.now(), extend_1: module },
    });
  },
};
